import { AddressHistory } from "./address-history-model";
import { BorrowerCounselingEvent } from "./borrower-counseling-event.model";
import { CreditInquiryDetails } from "./credit-inquiry-details.model";
import { Declarations } from "./declarations-model";
import { Demographics } from "./demographics-model";
import { Employment } from "./employment-model";
import { OwnOrRent } from "./enums/own-rent.enum";
import { ResidencyType } from "./enums/residency-type.enum";
import { IHaveName } from "./have-name.interface";
import { Income } from "./income-model";
import { ResidencyAddress } from "./residency-address.model";
import { DomesticRelationshipTypeEnum, MaritalStatusTypeEnum } from "./mortgage.model";

/**
 * WI 24451 / OPE-1 — marital statuses that expose the unmarried-partner (URLA "Basic Details") group.
 * 'Other' was added alongside 'Single' (OPE-1): an applicant who is neither married nor separated may still
 * be in a domestic partnership / civil union, so both statuses follow the exact same workflow.
 * Single source of truth for the gate + its cleanup cascade on My Info and the co-borrower mini form.
 */
export const UNMARRIED_PARTNER_MARITAL_STATUSES: ReadonlySet<string> = new Set<string>([
  MaritalStatusTypeEnum.Single,
  MaritalStatusTypeEnum.Other,
]);

/** True when the given marital status should offer the unmarried-partner questions. */
export function allowsUnmarriedPartner(maritalStatus: string | undefined | null): boolean {
  return !!maritalStatus && UNMARRIED_PARTNER_MARITAL_STATUSES.has(maritalStatus);
}

export class Borrower implements IHaveName {
  borrowerId: number | undefined;
  contactId: number | undefined;
  firstName: string = "";
  middleName: string = "";
  lastName: string = "";
  nameSuffix: string = "";
  primaryEmail: string = "";
  mobilePhone: string = "";
  homePhone: string = "";
  workPhone: string = "";
  phoneNumber: string = "";
  ownOrRent: OwnOrRent | undefined;
  creditScore: number | undefined;
  currentAddressHistory: AddressHistory | undefined;
  nonEmploymentIncomes: Income[] = [];
  employments: Employment[] = [];
  declarations: Declarations | undefined;
  governmentMonitors: Demographics;
  residencyAddresses: ResidencyAddress[] = [];
  counselingEvents: BorrowerCounselingEvent[] = [];
  dateOfBirth: string = "";
  socialSecNum: string = "";
  maritalStatus: string | undefined | null = null;
  dependents: number[] = [];
  hasServedInMilitary: boolean = false;
  militaryTourEndDate: string = "";
  branchOfService: string | null = null;
  isFirstUseOfVaBenefit: boolean = false;
  isVaFundingFeeExempt: boolean = false;
  isSurvivingSpouseOfMilitary: boolean = false;
  isActiveDutyMilitary: boolean = false;
  onlyNonActivatedReserveOrNationalGuard: boolean = false;
  isRetiredDischargedSeparatedFromMilitary: boolean = false;
  militaryServicePeriods: MilitaryServicePeriod[] = [];

  //not in the model
  permissionToRunCredit: boolean | undefined;
  creditInquiry: CreditInquiryDetails | undefined;
  permissionToRunIncome: boolean | undefined;
  willBeEdited: boolean = false;
  isPrimary: boolean;
  jointWithBorrowerId?: number | undefined;
  printApplicationIndex?: number | undefined;
  authorizedCreditCheck?: boolean | undefined;
  dateAuthorizedCreditCheck?: string | undefined;
  authorizationMethod?: string | undefined;
  hasValidEConsent?: boolean | undefined;
  hasAcknowledgedAbad?: boolean | undefined;

  languagePreference?: PredefinedLanguagePreference | string | null = null;
  languagePreferenceOtherDescription?: string;
  mortgagePartyType?: string | null = null;

  // WI 24451 — unmarried-partner (URLA "Basic Details") fields; shown on My Info when
  // allowsUnmarriedPartner(maritalStatus). Names match MortgageBorrower so they persist by name.
  domesticRelationshipType?: DomesticRelationshipTypeEnum | null = null;
  unmarriedRelationshipState?: string | null = null;
  unmarriedRelationshipTypeOther?: string | null = null;

  isMarriedToPrimaryBorrower?: boolean | null = null;

  constructor() {
    const currentAddress = new ResidencyAddress(ResidencyType.PresentAddress);
    this.residencyAddresses?.push(currentAddress);
    this.declarations = new Declarations();
    this.governmentMonitors = new Demographics();
    this.maritalStatus = null;
    this.languagePreference = null;
    this.nameSuffix = null;
    this.hasServedInMilitary = null;
  }
}

export class MilitaryServicePeriod {
  militaryServicePeriodId: number = 0;
  borrowerId: number = 0;
  branchOfService?: string;
  branchOfServiceOtherDescription?: string;
  militaryStatus?: string;
  militaryStatusOtherDescription?: string;
  militaryTourStartDate?: string;
  militaryTourEndDate?: string;
  militaryServiceNumber?: string;
  militaryServedAsName?: string;
}

export type PredefinedLanguagePreference =
  | 'Chinese'
  | 'English'
  | 'Korean'
  | 'Spanish'
  | 'Tagalog'
  | 'Vietnamese'
  | 'Other'
  ;
